import { type ReactElement } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert } from '@marketplace/ui';
import { type ProviderSubscription, type SubscriptionPlans } from '@marketplace/contracts';
import { BillingButton } from './BillingButton.js';
import { PastDueBanner } from './PastDueBanner.js';
import { PlanPrice } from './PlanPrice.js';
import { KindMismatch, SwitchPlan } from './SwitchPlan.js';
import {
  canSwitch,
  formatPeriodEnd,
  isLive,
  isPastDue,
  otherKind,
  payoutsStepPath,
  planFor,
  type BillingFailure,
  type CheckoutFrom,
  type PlanKind,
} from './billing.js';

export interface BillingPageProps {
  locale: string;
  subscription: ProviderSubscription;
  /** The provider profile's kind: what a new subscription is priced by (§4.3 step 4). */
  kind: PlanKind;
  /** `null` when the plans could not be read: nothing is sold without its price. */
  plans: SubscriptionPlans | null;
  /** A refused Subscribe, Manage billing or Update card, from the route's action. */
  failure: BillingFailure | null;
  /** Back from Checkout while the status can still change: Subscribe waits (§5 row 2). */
  watching: boolean;
  /** The watch ran its minute and the webhook has not landed. */
  pending: boolean;
  /** Back from Checkout's cancel. */
  cancelled: boolean;
  /**
   * `W5-T13` §4.6 — the page is the subscription step of becoming a provider (`?step=`, `?return=` or
   * `?cancelled=registration`): **Subscribe** starts Checkout from there, and comes back there.
   */
  registration: boolean;
}

/**
 * `W5-T07` §4.10 — the billing page's body.
 *
 * With no live subscription it is the offer: the plan for the profile's kind, its price and tax as the
 * server sent them, and **Subscribe**. With one, it is the subscription: plan, price, status line, the
 * renewal or end date, **Manage billing**, and **Switch** when a switch is allowed (§4.4 step 2).
 * Every word is a key; every amount and date is `Intl`'s, in the reader's language.
 */
export function BillingPage(props: BillingPageProps): ReactElement {
  const { t } = useTranslation();
  const { locale, failure, pending, cancelled, registration, subscription } = props;
  const from: CheckoutFrom = registration ? 'registration' : 'account';

  return (
    <article data-testid="billing">
      <h1>{t('billing.title')}</h1>
      {registration && !subscription.entitled ? <Registration cancelled={cancelled} /> : null}
      {cancelled ? <p className="mp-lead">{t('billing.return.cancelled')}</p> : null}
      {pending ? <p className="mp-lead">{t('billing.return.pending')}</p> : null}
      {failure === null ? null : <Failure locale={locale} failure={failure} from={from} />}
      {isLive(subscription.status) ? <Subscription {...props} /> : <Offer {...props} from={from} />}
      {registration && subscription.entitled ? (
        // `W5-T13` AC 27: subscribed, so on to the last step, `W5-T01`'s, unchanged.
        <p>
          <Link to={payoutsStepPath(locale)}>{t('billing.gate.continue_payouts')}</Link>
        </p>
      ) : null}
    </article>
  );
}

/**
 * `W5-T13` §4.6 — the step's header: which step it is, and why there is no way round it (ADR-017 §1:
 * being a provider *is* the subscription). No **Skip**, and no link anywhere else. Back from a
 * cancelled Checkout it says the one consequence instead: not listed until they subscribe.
 */
function Registration({ cancelled }: { cancelled: boolean }): ReactElement {
  const { t } = useTranslation();

  return (
    <section className="mp-section" data-testid="billing-registration">
      <p className="mp-meta">{t('billing.gate.registration_step')}</p>
      <p className="mp-lead">
        {t(cancelled ? 'billing.gate.not_listed' : 'billing.gate.registration_why')}
      </p>
    </section>
  );
}

/**
 * A refused Subscribe or portal, and the one way on that §7 gives it. A refused switch is the
 * dialog's own (`SwitchPlan`), so its `402` and **Update card** are shown there.
 */
function Failure({
  locale,
  failure,
  from,
}: {
  locale: string;
  failure: BillingFailure;
  from: CheckoutFrom;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <Alert tone="error">{t(failure)}</Alert>
      {failure === 'billing.error.exists' ? (
        <BillingButton locale={locale} intent="portal" label={t('billing.action.manage')} />
      ) : null}
      {failure === 'billing.error.not_found' ? (
        <BillingButton
          locale={locale}
          intent="subscribe"
          from={from}
          label={t('billing.action.subscribe')}
        />
      ) : null}
    </>
  );
}

function Offer({
  locale,
  subscription,
  kind,
  plans,
  watching,
  from,
}: BillingPageProps & { from: CheckoutFrom }): ReactElement {
  const { t } = useTranslation();
  const plan = planFor(plans, kind);

  return (
    <section className="mp-section">
      <h2 data-testid="billing-plan">{t(`billing.plan.${kind}`)}</h2>
      {plan === null ? (
        <Alert tone="error">{t('billing.error.unavailable')}</Alert>
      ) : (
        <PlanPrice plan={plan} locale={locale} />
      )}
      <p data-testid="billing-status">{t(`billing.status.${subscription.status}`)}</p>
      {plan === null || watching ? null : (
        <BillingButton
          locale={locale}
          intent="subscribe"
          from={from}
          label={t('billing.action.subscribe')}
        />
      )}
    </section>
  );
}

function Subscription({ locale, subscription, kind, plans }: BillingPageProps): ReactElement {
  const { t } = useTranslation();
  // A live row always carries its plan (§9); the profile's kind is only the fallback for a shape the
  // contract allows and the server does not send.
  const current = subscription.plan ?? kind;
  const plan = planFor(plans, current);
  const periodEnd = subscription.currentPeriodEnd;

  return (
    <section className="mp-section">
      <h2 data-testid="billing-plan">{t(`billing.plan.${current}`)}</h2>
      {plan === null ? null : <PlanPrice plan={plan} locale={locale} />}
      {isPastDue(subscription.status) ? (
        <PastDueBanner subscription={subscription} locale={locale} />
      ) : (
        <p data-testid="billing-status">{t(`billing.status.${subscription.status}`)}</p>
      )}
      {periodEnd === null ? null : (
        <p data-testid="billing-period">
          {t(subscription.cancelAtPeriodEnd ? 'billing.ends_on' : 'billing.renews_on', {
            date: formatPeriodEnd(periodEnd, locale),
          })}
        </p>
      )}
      {subscription.kindMismatch ? (
        <KindMismatch locale={locale} plan={current} profileKind={kind} />
      ) : null}
      <BillingButton
        locale={locale}
        intent="portal"
        label={t('billing.action.manage')}
        variant="secondary"
      />
      {canSwitch(subscription) ? (
        // Keyed by the target, so a switch that landed starts the next one from a fresh dialog.
        <SwitchPlan
          key={otherKind(current)}
          locale={locale}
          to={otherKind(current)}
          plans={plans}
        />
      ) : null}
    </section>
  );
}
